/**
 * PatientProfile — Account & Profile (Part D).
 *
 * Patients view & self-edit their personal, contact, next-of-kin and GP/NHS
 * details. Name + email stay read-only (account-controlled). Two save actions:
 *   • Personal + contact → PATCH /auth/me/contact
 *   • GP & NHS           → PATCH /patient/gp-info
 */
import { useEffect, useState } from "react";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { User, Phone, MapPin, HeartPulse, Stethoscope, Mail, CalendarDays } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";

function Field({ label, value, onChange, type = "text", placeholder, testid, disabled = false, maxLength }) {
  return (
    <label className="block">
      <span className="fl-label text-[11px]" style={{ color: "var(--fl-muted)" }}>{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className="fl-input mt-1 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid={testid}
      />
    </label>
  );
}

export default function PatientProfile() {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingContact, setSavingContact] = useState(false);
  const [savingGp, setSavingGp] = useState(false);

  const [contact, setContact] = useState({
    dob: "", phone: "", address_line1: "", address_line2: "", city: "", postcode: "", country: "",
    emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relationship: "",
  });
  const [gp, setGp] = useState({ gp_name: "", gp_surgery: "", gp_phone: "", gp_email: "", nhs_number: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      const [me, gpInfo] = await Promise.all([
        api.get("/auth/me").catch(() => ({ data: {} })),
        api.get("/patient/gp-info").catch(() => ({ data: {} })),
      ]);
      if (!alive) return;
      const u = me.data || {};
      setContact({
        dob: u.dob || "",
        phone: u.phone || "",
        address_line1: u.address_line1 || "",
        address_line2: u.address_line2 || "",
        city: u.city || "",
        postcode: u.postcode || "",
        country: u.country || "",
        emergency_contact_name: u.emergency_contact_name || "",
        emergency_contact_phone: u.emergency_contact_phone || "",
        emergency_contact_relationship: u.emergency_contact_relationship || "",
      });
      setGp({
        gp_name: gpInfo.data?.gp_name || "",
        gp_surgery: gpInfo.data?.gp_surgery || "",
        gp_phone: gpInfo.data?.gp_phone || "",
        gp_email: gpInfo.data?.gp_email || "",
        nhs_number: gpInfo.data?.nhs_number || "",
      });
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const setC = (k) => (v) => setContact((p) => ({ ...p, [k]: v }));
  const setG = (k) => (v) => setGp((p) => ({ ...p, [k]: v }));

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await api.patch("/auth/me/contact", contact);
      // Refresh the shared auth user so other screens see the new details.
      try { const { data } = await api.get("/auth/me"); setUser(data); } catch { /* noop */ }
      toast.success("Personal & contact details saved");
    } catch {
      toast.error("Couldn't save — please try again");
    } finally { setSavingContact(false); }
  };

  const saveGp = async () => {
    setSavingGp(true);
    try {
      await api.patch("/patient/gp-info", gp);
      toast.success("GP & NHS details saved");
    } catch {
      toast.error("Couldn't save — please try again");
    } finally { setSavingGp(false); }
  };

  return (
    <DashLayout>
      <div className="mb-6" data-testid="patient-profile-page">
        <div className="fl-label text-[#f47a20]">Account</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2 leading-tight">Account &amp; Profile</h1>
        <p className="text-zinc-400 mt-2 text-sm">
          Keep your details up to date so your clinician can always reach you and your records stay accurate.
        </p>
      </div>

      {loading ? (
        <div className="space-y-4" data-testid="profile-loading">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-56 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── Account (read-only) ── */}
          <Card className="fl-card p-0 border-white/10 bg-transparent" data-testid="profile-account-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="fl-heading text-xl flex items-center gap-2"><User size={18} className="text-[#f47a20]" /> Your account</CardTitle>
              <CardDescription className="text-sm text-zinc-400">Name and email are managed by the clinic. Contact us if either needs changing.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0 grid md:grid-cols-2 gap-4">
              <Field label="Full name" value={user?.name} disabled testid="profile-name" />
              <Field label="Email" value={user?.email} disabled testid="profile-email" />
            </CardContent>
          </Card>

          {/* ── Personal + contact ── */}
          <Card className="fl-card p-0 border-white/10 bg-transparent" data-testid="profile-contact-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="fl-heading text-xl flex items-center gap-2"><Phone size={18} className="text-[#f47a20]" /> Personal &amp; contact details</CardTitle>
              <CardDescription className="text-sm text-zinc-400">Your phone, address, date of birth and emergency contact.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Date of birth" type="date" value={contact.dob} onChange={setC("dob")} testid="profile-dob" />
                <Field label="Phone" type="tel" value={contact.phone} onChange={setC("phone")} placeholder="07123 456789" testid="profile-phone" maxLength={40} />
              </div>

              <div>
                <div className="fl-label text-xs mb-2 flex items-center gap-1.5" style={{ color: "var(--fl-muted-2)" }}><MapPin size={12} /> Address</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Address line 1" value={contact.address_line1} onChange={setC("address_line1")} testid="profile-address1" maxLength={200} />
                  <Field label="Address line 2" value={contact.address_line2} onChange={setC("address_line2")} testid="profile-address2" maxLength={200} />
                  <Field label="City / town" value={contact.city} onChange={setC("city")} testid="profile-city" maxLength={120} />
                  <Field label="Postcode" value={contact.postcode} onChange={setC("postcode")} testid="profile-postcode" maxLength={20} />
                  <Field label="Country" value={contact.country} onChange={setC("country")} testid="profile-country" maxLength={80} />
                </div>
              </div>

              <div>
                <div className="fl-label text-xs mb-2 flex items-center gap-1.5" style={{ color: "var(--fl-muted-2)" }}><HeartPulse size={12} /> Emergency contact</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <Field label="Name" value={contact.emergency_contact_name} onChange={setC("emergency_contact_name")} testid="profile-ec-name" maxLength={120} />
                  <Field label="Phone" type="tel" value={contact.emergency_contact_phone} onChange={setC("emergency_contact_phone")} testid="profile-ec-phone" maxLength={40} />
                  <Field label="Relationship" value={contact.emergency_contact_relationship} onChange={setC("emergency_contact_relationship")} placeholder="e.g. Partner" testid="profile-ec-rel" maxLength={60} />
                </div>
              </div>

              <Button onClick={saveContact} disabled={savingContact} className="bg-[#f47a20] hover:bg-[#d96812] text-black font-semibold disabled:opacity-60" data-testid="profile-save-contact">
                {savingContact ? "Saving…" : "Save personal & contact details"}
              </Button>
            </CardContent>
          </Card>

          {/* ── GP & NHS ── */}
          <Card className="fl-card p-0 border-white/10 bg-transparent" data-testid="profile-gp-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="fl-heading text-xl flex items-center gap-2"><Stethoscope size={18} className="text-[#f47a20]" /> GP &amp; NHS details</CardTitle>
              <CardDescription className="text-sm text-zinc-400">Helps your clinician coordinate care with your GP when needed.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="GP name" value={gp.gp_name} onChange={setG("gp_name")} testid="profile-gp-name" maxLength={120} />
                <Field label="GP surgery" value={gp.gp_surgery} onChange={setG("gp_surgery")} testid="profile-gp-surgery" maxLength={160} />
                <Field label="GP phone" type="tel" value={gp.gp_phone} onChange={setG("gp_phone")} testid="profile-gp-phone" maxLength={40} />
                <Field label="GP email" type="email" value={gp.gp_email} onChange={setG("gp_email")} testid="profile-gp-email" maxLength={160} />
                <Field label="NHS number" value={gp.nhs_number} onChange={setG("nhs_number")} placeholder="123 456 7890" testid="profile-nhs" maxLength={20} />
              </div>
              <Button onClick={saveGp} disabled={savingGp} className="bg-[#f47a20] hover:bg-[#d96812] text-black font-semibold disabled:opacity-60" data-testid="profile-save-gp">
                {savingGp ? "Saving…" : "Save GP & NHS details"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </DashLayout>
  );
}
